// Tab 3 · Auditoría: el feed de `GET /credit-plans/log`, un día por vez. Solo lectura.
// Orden cronológico descendente (el del backend), no alfabético: es una bitácora.
//
// El día se navega y las páginas se acumulan: la tabla muestra lo cargado y lo dice mientras
// queden asientos por traer. Antes se pedía la primera página del día en curso y no había forma
// de llegar ni al asiento 101 ni a los cambios de ayer.
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, ErrorState, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { SectionCard } from '@/shared/components/SectionCard';
import { auditFailureCopy, shiftDay } from '../logic';
import type { PolicyLogEntry } from '../types';

const COLUMNS = ['Fecha', 'Hora', 'Autor', 'Tipo', 'Objetivo', 'Cambio', 'Motivo'];

const KIND_BADGE: Record<string, { label: string; className: string }> = {
    plan: { label: 'Plan', className: 'bg-success/10 text-success' },
};

export interface AuditTabProps {
    entries: PolicyLogEntry[];
    /** Día del feed en hora del piso de venta (`YYYY-MM-DD`), igual que la partición del backend. */
    day: string;
    onDayChange: (day: string) => void;
    /** El día en curso; no se navega hacia adelante porque no hay partición futura. */
    today: string;
    /** El día trae más asientos de los que se han cargado. */
    hasMore: boolean;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
}

export function AuditTab({
    entries,
    day,
    onDayChange,
    today,
    hasMore,
    onLoadMore,
    isLoadingMore,
    isLoading,
    error,
    onRetry,
}: AuditTabProps) {
    const failure = error === null ? null : auditFailureCopy(error);

    return (
        <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
            <SectionCard
                title="Auditoría · cambios a planes de crédito"
                meta={
                    <span className="inline-flex items-center gap-1.5">
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2"
                            aria-label="Día anterior"
                            onClick={() => {
                                onDayChange(shiftDay(day, -1));
                            }}
                        >
                            <ChevronLeft className="size-3.5" aria-hidden />
                        </Button>
                        <span className="min-w-[110px] text-center text-[12px] font-semibold text-foreground">
                            {formatDateMx(day)}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2"
                            aria-label="Día siguiente"
                            disabled={day >= today}
                            onClick={() => {
                                onDayChange(shiftDay(day, 1));
                            }}
                        >
                            <ChevronRight className="size-3.5" aria-hidden />
                        </Button>
                    </span>
                }
                footnote={<AuditFootnote count={entries.length} hasMore={hasMore} />}
            >
                {isLoading ? (
                    <Loading text="Cargando bitácora…" />
                ) : failure ? (
                    <ErrorState
                        className="m-3"
                        title={failure.title}
                        description={failure.description}
                        onRetry={failure.canRetry ? onRetry : undefined}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-[10.5px]">
                            <thead>
                                <tr>
                                    {COLUMNS.map((column) => (
                                        <th
                                            key={column}
                                            scope="col"
                                            className="bg-muted/40 px-2.5 py-1.5 text-left text-[9px] font-bold uppercase tracking-wide text-muted-foreground"
                                        >
                                            {column}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {entries.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-2.5 py-5 text-center text-muted-foreground"
                                        >
                                            Sin cambios registrados
                                        </td>
                                    </tr>
                                ) : (
                                    entries.map((entry, index) => (
                                        <AuditRow
                                            key={`${entry.date}-${entry.time}-${String(index)}`}
                                            entry={entry}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                        {hasMore ? (
                            <div className="flex justify-center border-t border-border/40 px-2.5 py-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-3 text-[11px]"
                                    disabled={isLoadingMore}
                                    onClick={onLoadMore}
                                >
                                    {isLoadingMore ? 'Cargando…' : 'Cargar más'}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                )}
            </SectionCard>
        </div>
    );
}

/**
 * Cuántos asientos hay a la vista y si son todos los del día. Con páginas pendientes el número no
 * es un total, y decirlo es lo que separa una tabla honesta de un tope silencioso.
 */
function AuditFootnote({ count, hasMore }: { count: number; hasMore: boolean }) {
    if (count === 0) return null;
    const asientos = `${String(count)} ${count === 1 ? 'asiento' : 'asientos'}`;
    return (
        <span>
            {hasMore ? (
                <>
                    <strong>{asientos}</strong> cargados de este día — quedan más. Usa «Cargar más»
                    para traerlos.
                </>
            ) : (
                <>
                    <strong>{asientos}</strong> en el día, ya son todos.
                </>
            )}
        </span>
    );
}

/** El sufijo de versión del mock: `(v1→v2)` en una edición y `(nuevo v1)` en un alta. */
function versionSuffix(entry: PolicyLogEntry): string | null {
    if (entry.versionTo === null) return null;
    if (entry.versionFrom === null) return ` (nuevo v${String(entry.versionTo)})`;
    return ` (v${String(entry.versionFrom)}→v${String(entry.versionTo)})`;
}

function AuditRow({ entry }: { entry: PolicyLogEntry }) {
    const badge = KIND_BADGE[entry.kind] ?? {
        label: entry.kind,
        className: 'bg-muted text-muted-foreground',
    };
    const suffix = versionSuffix(entry);
    const cell = 'border-b border-border/40 px-2.5 py-1.5';

    return (
        <tr>
            <td className={cn(cell, 'tabular-nums')}>{entry.date}</td>
            <td className={cn(cell, 'tabular-nums')}>{entry.time}</td>
            {/* El autor viaja como lo asentó el backend: sin nombre resuelto es el cognitoSub
                crudo, que es verdad — un relleno inventaría quién firmó el cambio. */}
            <td className={cn(cell, 'break-all')}>{entry.author}</td>
            <td className={cell}>
                <span
                    className={cn(
                        'inline-flex items-center rounded-[3px] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide',
                        badge.className,
                    )}
                >
                    {badge.label}
                </span>
            </td>
            <td className={cn(cell, 'font-semibold')}>{entry.target}</td>
            <td className={cell}>
                {entry.change}
                {suffix === null ? null : <span className="text-muted-foreground">{suffix}</span>}
            </td>
            <td className={cn(cell, 'text-muted-foreground')}>{entry.reason}</td>
        </tr>
    );
}
